import React from 'react'
import Modal from '../../../../ui_reusable/Modal'
import { Button, Message } from 'rsuite'
import useStandardToaster from '../../../../ui_reusable/StandardToaster'
import { useQueryClient } from '@tanstack/react-query'
import type { MediaChannel } from '../../../../../api/generated/models'
import { useChannelsDestroy } from '../../../../../api/generated/endpoints/media-channels/media-channels'

interface DeleteChannelProps {
  channel: MediaChannel
  onSuccess: () => void
  open: boolean
  onClose: () => void
}

const DeleteChannel: React.FC<DeleteChannelProps> = ({
  channel,
  onSuccess,
  open,
  onClose,
}) => {
  const { pushToast } = useStandardToaster()
  const queryClient = useQueryClient()

  const deleteMutation = useChannelsDestroy({
    mutation: {
      onSuccess: () => {
        // Invalidate driver list queries to refresh the databook
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/channels/'],
        })

        onSuccess()
        pushToast(
          <Message type="success">
            {channel.name} has been deleted successfully
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
        onClose()
      },
      onError: (error: any) => {
        pushToast(
          <Message type="error">
            {error?.message || 'Failed to delete channel'}
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      },
    },
  })

  const handleSubmit = async () => {
    try {
      await deleteMutation.mutateAsync({
        id: channel.id!,
      })
    } catch (error: any) {
      // Error handling is done in onError callback
      console.error('Delete channel error:', error)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Delete ${channel.name}`}>
      <Modal.Body>
        <p>
          Deleting a channel will also delete the related datasets. Do you want
          to proceed?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          onClick={handleSubmit}
          appearance="primary"
          color="violet"
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Yes'}
        </Button>
        <Button
          type="button"
          onClick={onClose}
          appearance="subtle"
          disabled={deleteMutation.isPending}
        >
          No
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DeleteChannel
